// Copyright (c) Ping. All rights reserved.
//
// Web host for the /tasks tab. Wires the shared `<TasksScreen>` to the
// live DeviceGroup-only task list:
//
//   * `useTasks(webTasksPersister)` is the live source — a Dexie
//     `liveQuery` that re-renders on every local write AND on every
//     incoming `com.ping.task_upsert` / `task_delete` the foundations'
//     `useIncomingMessages` applies (cross-device mirror, §2.10).
//   * `useTaskRecordScreenProps` adapts that `TaskRecord[]` onto the
//     screen's `load` / `subscribe` / `update` / bulk contract.
//   * Mutations are LOCAL-FIRST: write through `webTasksPersister`
//     (works fully offline / pre-SDK), then best-effort mirror to the
//     DeviceGroup via `sendTaskUpsert` / `sendTaskDelete`.

import { useMemo } from 'react';

import {
  type TaskRecord,
  type TaskRecordMutators,
  TasksScreen,
  useSession,
  useTaskRecordScreenProps,
  useTasks,
} from '@ping/ui';

import { webTasksPersister } from '@core/persistence/db';
import { sendTaskDelete, sendTaskUpsert } from '@features/tasks/deviceGroupTaskSync';

export function TasksRouteHost(): JSX.Element {
  const { client } = useSession();
  const rows = useTasks(webTasksPersister);

  const mutators = useMemo<TaskRecordMutators>(
    () => ({
      upsert: async (record: TaskRecord) => {
        // Local-first: the Dexie write is authoritative and drives the
        // live list; the DeviceGroup mirror is best-effort.
        await webTasksPersister.put(record);
        void sendTaskUpsert(client, record);
      },
      remove: async (id: string) => {
        await webTasksPersister.delete(id);
        void sendTaskDelete(client, id);
      },
    }),
    [client],
  );

  const screenProps = useTaskRecordScreenProps(rows, mutators);

  return <TasksScreen {...screenProps} />;
}
